import React, { useState, useEffect, useCallback } from 'react';
import { Search, Trash2, Eye, Users, Loader2, AlertCircle, RefreshCw } from 'lucide-react';
import ProfileCard from '../components/ProfileCard';
import AlignmentCard from '../components/AlignmentCard';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const ProfilesPage = () => {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProfile, setSelectedProfile] = useState(null);

  const fetchProfiles = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API}/profiles`);
      setProfiles(response.data);
    } catch (err) {
      console.error('Error fetching profiles:', err);
      setError('Error al cargar los perfiles');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProfiles();

    const refreshProfiles = () => fetchProfiles();
    const refreshVisibleProfiles = () => {
      if (!document.hidden) fetchProfiles();
    };

    window.addEventListener('focus', refreshProfiles);
    window.addEventListener('storage', refreshProfiles);
    window.addEventListener('lastwar-profiles-updated', refreshProfiles);
    document.addEventListener('visibilitychange', refreshVisibleProfiles);

    return () => {
      window.removeEventListener('focus', refreshProfiles);
      window.removeEventListener('storage', refreshProfiles);
      window.removeEventListener('lastwar-profiles-updated', refreshProfiles);
      document.removeEventListener('visibilitychange', refreshVisibleProfiles);
    };
  }, [fetchProfiles]);

  const deleteProfile = async (id) => {
    if (!window.confirm('¿Estás seguro de eliminar este perfil?')) return;
    
    try {
      await axios.delete(`${API}/profiles/${id}`);
      setProfiles(profiles.filter(p => p.id !== id));
      if (selectedProfile?.id === id) setSelectedProfile(null);
    } catch (err) {
      console.error('Error deleting profile:', err);
      setError('Error al eliminar el perfil');
    }
  };

  const filteredProfiles = profiles.filter(profile => 
    profile.playerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    profile.allianceName?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-5xl font-black text-white mb-2" style={{textShadow: '0 0 20px rgba(255,255,255,0.2)'}}>
            Perfiles <span className="text-cyan-400">Guardados</span>
          </h1>
          <p className="text-cyan-300/70">
            {loading ? 'Cargando perfiles...' : `${profiles.length} perfiles en tu colección`}
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-cyan-400" />
            <input
              type="text"
              placeholder="Buscar perfil..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              data-testid="search-profile-input"
              className="pl-11 pr-4 py-3 bg-[#0a1628]/80 border-2 border-cyan-500/30 rounded-xl text-white placeholder:text-cyan-500/50 focus:border-cyan-400 focus:outline-none w-64 transition-colors"
            />
          </div>
          <button
            onClick={fetchProfiles}
            data-testid="refresh-profiles-btn"
            className="game-btn game-btn-blue p-3"
          >
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-10 h-10 text-orange-400 animate-spin" />
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="game-card border-red-500 p-6">
          <div className="flex items-center gap-3">
            <AlertCircle className="w-6 h-6 text-red-500" />
            <span className="text-cyan-100">{error}</span>
          </div>
        </div>
      )}

      {/* Empty State */}
      {!loading && !error && profiles.length === 0 && (
        <div className="game-card p-12 text-center">
          <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-cyan-500/20 to-blue-500/20 rounded-2xl flex items-center justify-center border-2 border-cyan-500/30">
            <Users className="w-12 h-12 text-cyan-400" />
          </div>
          <h3 className="text-2xl font-bold text-white mb-3">No hay perfiles guardados</h3>
          <p className="text-cyan-300/70 mb-6">Extrae tu primer perfil para verlo aquí</p>
          <button className="game-btn">
            EXTRAER PERFIL
          </button>
        </div>
      )}

      {/* Profiles Grid */}
      {!loading && filteredProfiles.length > 0 && (
        <div className="grid md:grid-cols-2 gap-6">
          {/* Profile List */}
          <div className="space-y-4">
            <h2 className="text-xl font-bold text-cyan-300 mb-4">Lista de Perfiles</h2>
            {filteredProfiles.map((profile) => (
              <div key={profile.id} className="relative group">
                <ProfileCard 
                  profile={profile} 
                  compact 
                  onClick={() => setSelectedProfile(profile)}
                />
                <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity flex gap-2">
                  <button
                    onClick={() => setSelectedProfile(profile)}
                    data-testid={`view-profile-${profile.id}`}
                    className="p-2 bg-cyan-500 hover:bg-cyan-400 rounded-lg transition-colors border border-cyan-300"
                  >
                    <Eye className="w-4 h-4 text-white" />
                  </button>
                  <button
                    onClick={() => deleteProfile(profile.id)}
                    data-testid={`delete-profile-${profile.id}`}
                    className="p-2 bg-red-500 hover:bg-red-400 rounded-lg transition-colors border border-red-300"
                  >
                    <Trash2 className="w-4 h-4 text-white" />
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Selected Profile Detail */}
          <div className="sticky top-8">
            <h2 className="text-xl font-bold text-cyan-300 mb-4">Detalle del Perfil</h2>
            {selectedProfile ? (
              <div className="space-y-6">
                <ProfileCard profile={selectedProfile} />

                {selectedProfile.defensiveAlignment ? (
                  <div>
                    <h2 className="text-xl font-bold text-cyan-300 mb-4">Muralla / Alineacion defensiva</h2>
                    <AlignmentCard alignment={selectedProfile.defensiveAlignment} />
                  </div>
                ) : (
                  <div className="game-card p-6 text-center">
                    <p className="text-cyan-300/80">Este perfil todavia no tiene datos de muralla guardados.</p>
                  </div>
                )}
              </div>
            ) : (
              <div className="game-card p-12 text-center">
                <Eye className="w-14 h-14 mx-auto mb-4 text-cyan-600/50" />
                <p className="text-cyan-500/70">Selecciona un perfil para ver el detalle</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* No results */}
      {!loading && searchTerm && filteredProfiles.length === 0 && profiles.length > 0 && (
        <div className="game-card p-10 text-center">
          <Search className="w-14 h-14 mx-auto mb-4 text-cyan-600/50" />
          <h3 className="text-xl font-bold text-white mb-2">Sin resultados</h3>
          <p className="text-cyan-300/70">No se encontraron perfiles que coincidan con "{searchTerm}"</p>
        </div>
      )}
    </div>
  );
};

export default ProfilesPage;
